import {
  LayoutDashboard,
  MessageSquare,
  FileOutput,
  Search,
  Settings,
  Briefcase,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useTheme } from "next-themes";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSidebar } from "@/components/ui/sidebar/context";

const mainMenuItems = [
  {
    title: "Dashboard",
    icon: LayoutDashboard,
    url: "/",
    description: "Oversikt og nøkkelinformasjon"
  },
  {
    title: "AdvoKaiChat",
    icon: MessageSquare,
    url: "/assistant",
    description: "AI-drevet juridisk rådgivning"
  },
  {
    title: "DokGen",
    icon: FileOutput,
    url: "/documents",
    description: "Automatisert dokumentgenerering"
  },
  {
    title: "Saker",
    icon: Briefcase,
    url: "/cases",
    description: "Saksbehandling og oppfølging"
  },
  {
    title: "LovSøk",
    icon: Search,
    url: "/search",
    description: "Søk i lover og forskrifter"
  },
];

export function AppSidebar() {
  const location = useLocation();
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const { open, openMobile, setOpenMobile } = useSidebar();
  const isOpen = isMobile ? openMobile : open;

  const handleLinkClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  if (!isOpen && isMobile) {
    return null;
  }

  return (
    <aside 
      className={cn(
        "fixed top-0 left-0 z-[100] h-screen",
        "bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
        "border-r transition-all duration-300",
        isOpen ? "w-[280px]" : "w-0 md:w-[60px]",
        "motion-safe:animate-in motion-safe:fade-in-0"
      )}
      style={{
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      <div className="p-4 border-b border-border flex justify-between items-center h-16">
        {isOpen && (
          <img 
            src="/lovable-uploads/5e717973-e7bf-41d2-b8b7-f96729ec97a6.png" 
            alt="AdvoKai Logo" 
            className="h-8 w-auto"
          />
        )}
      </div>

      <nav className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-accent scrollbar-track-transparent">
        <div className="space-y-1 p-2">
          {mainMenuItems.map((item) => (
            <TooltipProvider key={item.title}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to={item.url}
                    onClick={handleLinkClick}
                    className={cn(
                      "flex items-center gap-3 w-full p-3 rounded-lg transition-all duration-200",
                      "hover:bg-accent hover:text-accent-foreground",
                      location.pathname === item.url 
                        ? "bg-accent text-accent-foreground" 
                        : "text-muted-foreground",
                      !isOpen && "justify-center",
                      isMobile && "text-base"
                    )}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    {isOpen && (
                      <span className="font-medium truncate">{item.title}</span>
                    )}
                  </Link>
                </TooltipTrigger>
                {!isOpen && !isMobile && (
                  <TooltipContent side="right" sideOffset={10}>
                    <p className="font-medium">{item.title}</p>
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>
      </nav>

      <div className="mt-auto border-t border-border p-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/settings"
                onClick={handleLinkClick}
                className={cn(
                  "flex items-center gap-3 w-full p-3 rounded-lg transition-all duration-200",
                  "hover:bg-accent hover:text-accent-foreground",
                  location.pathname === "/settings"
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground",
                  !isOpen && "justify-center",
                  isMobile && "text-base"
                )}
              >
                <Settings className="h-5 w-5 flex-shrink-0" />
                {isOpen && (
                  <div className="flex flex-col">
                    <span className="font-medium">Innstillinger</span>
                    <span className="text-xs text-muted-foreground">Tilpass systemet</span>
                  </div>
                )}
              </Link>
            </TooltipTrigger>
            {!isOpen && !isMobile && (
              <TooltipContent side="right" sideOffset={10}>
                <p className="font-medium">Innstillinger</p>
                <p className="text-xs text-muted-foreground">Tilpass systemet</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>
    </aside>
  );
}
